'use client';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { sampleSize } from 'lodash-es';
import { Theme, useMediaQuery } from '@mildang/mui';
import { useMemo, useEffect, useCallback } from 'react';
import { createParser, useQueryState } from 'nuqs';
import { parseAsBoolean } from 'nuqs';
import { ListViewMode } from '../utils/types';

type ChatSidebarViewType = 'CHANNEL' | 'CHANNEL_LIST';

interface ChatSidebarStore {
  isInitialized: boolean;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  activeChannelId: string | null;
  setActiveChannelId: (channelId: string | null) => void;
  viewType: ChatSidebarViewType;
  setViewType: (viewType: ChatSidebarViewType) => void;
  initState: (isSidebarOpen: boolean, activeChannelId: string | null, viewType: ChatSidebarViewType) => void;
  setIsSidebarOpen: (isSidebarOpen: boolean) => void;
}

export const useChatSidebarStore = create<ChatSidebarStore>()(
  persist(
    (set) => ({
      isSidebarOpen: false,
      isInitialized: false,
      initState: (isSidebarOpen, activeChannelId, viewType) =>
        set((state) => {
          if (state.isInitialized) state;
          return { isSidebarOpen, activeChannelId, viewType, isInitialized: true };
        }),
      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
      activeChannelId: null,
      setActiveChannelId: (channelId) => set(() => ({ activeChannelId: channelId, viewType: 'CHANNEL' })),
      setViewType: (viewType) => set({ viewType }),
      viewType: 'CHANNEL_LIST',
      setIsSidebarOpen: (isSidebarOpen) => set({ isSidebarOpen }),
    }),
    {
      name: 'chat-sidebar-store',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

interface ChatScrollStore {
  scrollCursor?: string;
  setChatScrollCursor: (id?: string) => void;
}

export const useChatScrollStore = create<ChatScrollStore>()((set) => ({
  scrollCursor: undefined,
  setChatScrollCursor: (id) => set({ scrollCursor: id }),
}));

interface ChatChannelListViewStore {
  listViewMode: ListViewMode;
  setListViewMode: (mode: ListViewMode) => void;
  toggleListViewMode: () => void;
}

export const useChatChannelListViewStore = create<ChatChannelListViewStore>()(
  persist(
    (set) => ({
      listViewMode: 'list',
      setListViewMode: (mode) => set({ listViewMode: mode }),
      toggleListViewMode: () => set((state) => ({ listViewMode: state.listViewMode === 'list' ? 'group' : 'list' })),
    }),
    {
      name: 'chat-channel-list-view-store',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

interface ChatChannelGroupExpandedStore {
  expandedMap: Record<string, boolean>;
  toggleExpanded: (groupId: string) => void;
}

export const useChatChannelGroupExpandedStore = create<ChatChannelGroupExpandedStore>()(
  persist(
    (set) => ({
      expandedMap: {},
      toggleExpanded: (groupId) =>
        set((state) => ({
          expandedMap: {
            ...state.expandedMap,
            [groupId]: !state.expandedMap[groupId],
          },
        })),
    }),
    {
      name: 'chat-channel-group-expanded-store',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

const encode = (str: string) => btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const decode = (b64: string) =>
  atob(b64.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64.length + 3) % 4));

export const parseAsGlobalId = createParser<string>({
  parse(v) {
    if (!v) return null;
    const [_ran, id] = decode(v).split(':');

    return id;
  },
  serialize(id) {
    return id
      ? encode(
          `${sampleSize('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', 30).join(
            '',
          )}:${id}`,
        )
      : '';
  },
});

export function useIsSidebarOpenQueryState() {
  const isMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  return useQueryState('cho', parseAsBoolean.withOptions({ history: isMdDown ? 'push' : undefined }));
}

export function useActiveChannelIdQueryState() {
  const isMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  return useQueryState('cai', parseAsGlobalId.withOptions({ history: isMdDown ? 'push' : undefined }));
}

export function useChatSidebarSyncRouteState() {
  const isMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { initState, setIsSidebarOpen, setActiveChannelId } = useChatSidebarStore();

  const [isSidebarOpenQuery] = useIsSidebarOpenQueryState();
  const [activeChannelIdQuery] = useActiveChannelIdQueryState();

  useEffect(() => {
    // if (!isMdDown) {
    //   return;
    // }
    if (!isSidebarOpenQuery) {
      initState(false, null, 'CHANNEL_LIST');
    } else {
      initState(isSidebarOpenQuery, activeChannelIdQuery, activeChannelIdQuery ? 'CHANNEL' : 'CHANNEL_LIST');
    }
  }, [initState, isSidebarOpenQuery, activeChannelIdQuery]);

  useEffect(() => {
    if (!isMdDown) {
      return;
    }
    setIsSidebarOpen(!!isSidebarOpenQuery);
  }, [isSidebarOpenQuery, setIsSidebarOpen]);

  useEffect(() => {
    if (!isMdDown) {
      return;
    }
    if (activeChannelIdQuery) {
      setActiveChannelId(activeChannelIdQuery);
    }
  }, [activeChannelIdQuery, setActiveChannelId]);
}

export function useChatSidebarToggleSidebar() {
  const { isSidebarOpen, toggleSidebar } = useChatSidebarStore();
  const [_, setIsSidebarOpen] = useIsSidebarOpenQueryState();
  const isMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  return useCallback(
    function toggleSidebarWithQuery() {
      toggleSidebar();
      if (!isMdDown) {
        return;
      }
      if (isSidebarOpen) {
        setIsSidebarOpen(null);
      } else {
        setIsSidebarOpen(true);
      }
    },
    [isSidebarOpen, setIsSidebarOpen, toggleSidebar, isMdDown],
  );
}

export function useChatSidebarSetActiveChannelId() {
  const { setActiveChannelId, setViewType, isInitialized } = useChatSidebarStore();
  const [_, setActiveChannelIdQuery] = useActiveChannelIdQueryState();
  const isMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return useMemo(() => {
    return {
      goToChannel: function setActiveChannelIdWithQuery(channelId: string) {
        if (isMdDown && !isInitialized) return;
        setActiveChannelId(channelId);
        if (isMdDown) {
          setActiveChannelIdQuery(channelId);
        }
      },
      goToChannelList: function setActiveChannelIdWithQuery() {
        if (isMdDown && !isInitialized) return;
        setViewType('CHANNEL_LIST');
        if (isMdDown) {
          setActiveChannelIdQuery(null);
        }
      },
    };
  }, [setActiveChannelId, setActiveChannelIdQuery, setViewType, isInitialized]);
}
